"use client";

import { ArrowRight, Calculator, Wallet, BookOpen, ChartLine as LineChart, Compass, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurCoreDivisions() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    number: "01",
    title: "Construction Management",
    description: "End-to-end oversight of physical builds, ensuring structural integrity and architectural precision from ground-breaking to completion.",
    Icon: Calculator
  }, {
    number: "02",
    title: "Systems Integration",
    description: "Seamlessly connecting your physical infrastructure with digital workflows to optimize operational efficiency and site security.",
    Icon: Wallet
  }, {
    number: "03",
    title: "Software Development",
    description: "Custom-engineered applications and platforms designed to streamline your business processes and enhance user engagement.",
    Icon: BookOpen
  }, {
    number: "04",
    title: "Data Analytics",
    description: "Transforming raw operational data into actionable insights that drive smarter decision-making and long-term growth.",
    Icon: LineChart
  }, {
    number: "05",
    title: "Strategic Consulting",
    description: "Expert guidance on scaling your technology and construction assets to meet the demands of a modern market.",
    Icon: Compass
  }, {
    number: "06",
    title: "Quality Assurance",
    description: "Rigorous testing and compliance protocols to ensure every project meets the highest standards of safety and performance.",
    Icon: ShieldCheck
  }];
  return <section data-section-id="241" id="our-core-divisions" className="bg-background text-foreground py-20 md:py-28 lg:py-32 light">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono italic text-base text-muted-foreground/70">02</span>
              <span className="h-px w-8 bg-border" />
              <span className="font-mono uppercase tracking-widest text-xs text-muted-foreground">
                Our Expertise
              </span>
            </div>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Bridging the gap between{" "}
              <span className="italic font-light text-primary">code and concrete.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-md lg:ml-auto">
              NYLA Tech delivers a unified approach to business development. We combine 
              heavy-duty construction expertise with cutting-edge technology solutions 
              to build the future of your enterprise.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div key={i} data-index={i} className={`group relative bg-card p-8 md:p-10 border border-transparent hover:border-primary transition-all duration-500 ease-out hover:-translate-y-0.5 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms"
          }}>
                {}
                <span className="absolute top-6 right-6 font-mono italic text-3xl text-muted-foreground/30 group-hover:text-primary transition-colors duration-500">
                  {service.number}
                </span>

                {}
                <div className="mb-10">
                  <Icon className="w-10 h-10 text-primary" strokeWidth={1.25} aria-hidden="true" />
                </div>

                {}
                <h3 className="font-default font-bold text-2xl text-foreground mb-3 leading-snug">
                  {service.title}
                </h3>

                {}
                <p className="text-sm md:text-base leading-relaxed text-muted-foreground mb-8 max-w-sm">
                  {service.description}
                </p>

                {}
                <Link to="/HomePage" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-foreground hover:text-primary transition-colors duration-300">
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>;
        })}
        </div>

        {}
        <div ref={ctaRef} className={`mt-16 md:mt-20 text-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="link" className="text-foreground hover:text-primary font-mono uppercase tracking-wider text-sm h-auto p-0">
            <Link to="/HomePage" className="inline-flex items-center gap-2 group">
              <span className="border-b border-border group-hover:border-primary pb-1 transition-colors duration-300">
                View all capabilities
              </span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}